import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { API_BASE_URL } from '../config';

function CustomPageView({ pageSlug, onBack }) {
  const { t } = useTranslation();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!pageSlug) {
      setError('Page slug is required');
      setLoading(false);
      return;
    }

    fetchPage();
  }, [pageSlug]);

  const fetchPage = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${API_BASE_URL}/pages/${pageSlug}`);
      
      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Страница не найдена');
        }
        throw new Error('Ошибка загрузки страницы');
      }

      const pageData = await response.json();
      setPage(pageData);
    } catch (err) {
      console.error('Error fetching page:', err);
      setError(err.message || 'Произошла ошибка при загрузке страницы');
    } finally {
      setLoading(false);
    }
  };

  // Sanitize and render HTML content
  const renderContent = (htmlContent) => {
    if (!htmlContent) return null;

    // Basic HTML sanitization - only allow safe tags
    const allowedTags = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'br', 'strong', 'b', 'em', 'i', 'u', 'ul', 'ol', 'li', 'a', 'span', 'div'];
    
    // Simple sanitization - in production, consider using a library like DOMPurify
    let sanitizedContent = htmlContent;
    
    // Convert line breaks to <br> tags if not already HTML
    if (!htmlContent.includes('<') && htmlContent.includes('\n')) {
      sanitizedContent = htmlContent.split('\n').join('<br>');
    }

    return (
      <div 
        className="prose prose-gray max-w-none"
        dangerouslySetInnerHTML={{ __html: sanitizedContent }}
        style={{
          lineHeight: '1.6',
          fontSize: '16px',
          color: '#374151'
        }}
      />
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="flex flex-col items-center space-y-4">
          <div className="w-8 h-8 border-4 border-gray-200 border-t-gray-600 rounded-full animate-spin"></div>
          <div className="text-gray-500 text-base">Загрузка страницы...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="text-6xl mb-4">😞</div>
          <div className="text-xl text-gray-700 mb-4">{error}</div>
          <button
            onClick={fetchPage}
            className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            Попробовать еще раз
          </button>
        </div>
      </div>
    );
  }

  if (!page) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="text-6xl mb-4">📄</div>
          <div className="text-xl text-gray-700">Страница не найдена</div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Page Header */}
      <div className="mb-8 text-center">
        {page.icon && (
          <div className="text-4xl mb-4">{page.icon}</div>
        )}
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          {page.title}
        </h1>
     </div>

      {/* Page Content */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
        <div className="p-8">
          {renderContent(page.content)}
        </div>
      </div>

    </div>
  );
}

export default CustomPageView;